import varType from '../enums/varType';

/**
 * Matches name@domain.tld, e.g. max.mustermann@enloc.de
 *
 * Before the @: letters, digits and . _ % + -
 * After the @: the domain, then a dot and an ending of 2 to 64 letters (.de, .com, ...)
 * No spaces anywhere, and nothing before or after the address.
 */
const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,64}$/;

/**
 * Validates that a value is present.
 *
 * Rules:
 * - `null` / `undefined` → invalid
 * - string → invalid if empty or whitespace-only
 * - array → invalid if empty
 *
 * @param {unknown} value - Value to validate
 * @param {string} [failureReturnValue] - Optional error message override
 * @returns {true|string} `true` if valid, otherwise an error message
 */
export function required(value, failureReturnValue) {
    const message = failureReturnValue ?? 'Pflichtfeld';

    if (value === null || value === undefined) return message;

    if (Array.isArray(value)) {
        return value.length === 0 ? message : true;
    }

    if (typeof value === varType.String) {
        return value.trim().length === 0 ? message : true;
    }

    return true;
}

/**
 * Validates that a value does not exceed a maximum length.
 * Empty values are treated as valid.
 *
 * @param {unknown} value - Value to validate
 * @param {number} maxLength - Maximum allowed length
 * @param {string} [failureReturnValue] - Optional error message override
 * @returns {true|string}
 */
export function maxLength(value, maxLength, failureReturnValue) {
    const message = failureReturnValue ?? `Eingabe ist zu lang (maximal ${maxLength} Zeichen)`;

    if (value === null || value === undefined) return true;

    const stringValue = String(value);
    return stringValue.length > maxLength ? message : true;
}

/**
 * Validates that a value meets a minimum length.
 * Empty values are treated as valid.
 *
 * @param {unknown} value - Value to validate
 * @param {number} minLength - Minimum required length
 * @param {string} [failureReturnValue] - Optional error message override
 * @returns {true|string}
 */
export function minLength(value, minLength, failureReturnValue) {
    const message = failureReturnValue ?? `Eingabe ist zu kurz (mindestens ${minLength} Zeichen)`;

    if (value === null || value === undefined) return true;

    const stringValue = String(value);
    return stringValue.length < minLength ? message : true;
}

/**
 * Validates that a value has an exact length.
 * Empty values are treated as valid.
 *
 * @param {unknown} value - Value to validate
 * @param {number} length - Required exact length
 * @param {string} [failureReturnValue] - Optional error message override
 * @returns {true|string}
 */
export function exactLength(value, length, failureReturnValue) {
    const message = failureReturnValue ?? `Eingabe muss genau ${length} Zeichen lang sein.`;

    if (value === null || value === undefined) return true;

    const stringValue = String(value);
    return stringValue.length !== length ? message : true;
}


/**
 * Validates that a numeric value lies within a given range.
 * Empty values are treated as valid.
 *
 * @param {unknown} value - Value to validate
 * @param {number} minValue - Minimum allowed value
 * @param {number} [maxValue] - Optional maximum allowed value
 * @param {string} [failureReturnValue] - Optional error message override
 * @returns {true|string}
 */
export function numberConstraints(value, minValue, maxValue, failureReturnValue) {
    const message =
        failureReturnValue ??
        `Wert liegt außerhalb des zulässigen Bereichs (mindestens ${minValue}${maxValue !== undefined ? ` und maximal ${maxValue}` : ''})`;

    if (value === null || value === undefined || value === '') return true;

    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return message;

    if (maxValue === undefined) {
        return numericValue < minValue ? message : true;
    }

    return numericValue < minValue || numericValue > maxValue ? message : true;
}

/**
 * Validates that a value is a positive number (>= 0).
 * Empty values are treated as valid.
 *
 * @param {unknown} value - Value to validate
 * @param {string} [failureReturnValue] - Optional error message override
 * @returns {true|string}
 */
export function onlyPositiveNumbers(value, failureReturnValue) {
    const message = failureReturnValue ?? 'Wert ist keine positive Zahl';

    if (value === null || value === undefined || value === '') return true;

    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return message;

    return numericValue < 0 ? message : true;
}

/**
 * Validates that a value is an integer.
 * Empty values are treated as valid.
 *
 * @param {unknown} value - Value to validate
 * @param {string} [failureReturnValue] - Optional error message override
 * @returns {true|string}
 */
export function onlyInteger(value, failureReturnValue) {
    const message = failureReturnValue ?? 'Bitte geben Sie eine Ganzzahl ein';

    if (value === null || value === undefined || value === '') return true;

    const numericValue = Number(value);
    if (!Number.isInteger(numericValue)) return message;

    return true;
}

/**
 * Validates an email address format.
 * Empty values are treated as valid.
 *
 * @param {unknown} value - Value to validate
 * @param {string} [failureReturnValue] - Optional error message override
 * @returns {true|string}
 */
export function validEmail(value, failureReturnValue) {
    const message = failureReturnValue ?? 'Bitte geben Sie eine gültige Email-Adresse ein';

    if (value === null || value === undefined || value === '') return true;

    return emailRegex.test(String(value)) ? true : message;
}

/**
 * Validates one or more email addresses separated by comma.
 * Empty values are treated as valid.
 *
 * @param {any} value - Value to validate, e.g. 'a@b.de, c@d.de'
 * @param {string} [failureReturnValue] - Optional error message override
 * @returns {true|string}
 */
export function validEmails(value, failureReturnValue) {
    const message = failureReturnValue ?? 'Bitte geben Sie gültige Email-Adressen ein (mehrere durch Komma getrennt)';

    if (value === null || value === undefined || value === '') return true;

    return String(value)
        .split(',')
        .every(email => emailRegex.test(email.trim())) ? true : message;
}

/**
 * Validates an international phone number (E.164-like).
 * Empty values are treated as valid.
 *
 * @param {unknown} value - Value to validate
 * @param {string} [failureReturnValue] - Optional error message override
 * @returns {true|string}
 */
export function validPhone(value, failureReturnValue) {
    const message = failureReturnValue ?? 'Bitte geben Sie ein gültig Telefone ein (+4912345678901)';

    if (value === null || value === undefined || value === '') return true;

    const phoneRegex = /^[+]?[1-9]\d{6,14}$/;
    return phoneRegex.test(String(value)) ? true : message;
}

/**
 * Validates a meter count using comma as decimal separator.
 *
 * Supported formats:
 * - 12.456,5
 * - 12.456,00
 * - 123456
 * - 123456,5
 * - 123456,00
 *
 * Empty values are treated as valid.
 *
 * @param {unknown} value - Value to validate
 * @param {string} [failureReturnValue] - Optional error message override
 * @returns {true|string}
 */
export function meterCount(value, failureReturnValue) {
    const message = failureReturnValue ?? 'Bitte geben Sie einen validen Zählerstand an';

    if (value === null || value === undefined || value === '') return true;

    const meterRegex = /^(?:\d+(?:,\d{1,2})?|\d{1,3}(?:\.\d{3})+,\d{1,2})$/;
    
    return meterRegex.test(String(value)) ? true : message;
}
